/* eslint-disable import/no-extraneous-dependencies */
// ============================================================================
// ThreeComponentScene.tsx
// ============================================================================

/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useRef } from 'react';
import {
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  PCFSoftShadowMap,
  SRGBColorSpace,
  Object3D,
  Mesh,
  Material,
  Color,
  NoToneMapping,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export type ThreeComponentSceneReadyArgs = {
  renderer: WebGLRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  controls: OrbitControls;
  invalidateRender: () => void;
};

type ThreeComponentSceneParameters = {
  callbackOnSceneReady: (args: ThreeComponentSceneReadyArgs) => void;
};

const disposeMaterial = (material: Material | Material[] | undefined): void => {
  if (!material) return;

  if (Array.isArray(material)) {
    material.forEach((m) => m.dispose());
    return;
  }

  material.dispose();
};

const disposeObject = (object: Object3D): void => {
  object.traverse((child) => {
    const mesh = child as Mesh;

    if (mesh.geometry) {
      mesh.geometry.dispose();
    }

    disposeMaterial(mesh.material as Material | Material[] | undefined);
  });
};

export function ThreeComponentScene(
  {
    callbackOnSceneReady,
  }: ThreeComponentSceneParameters,
): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const rendererRef = useRef<WebGLRenderer | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const cameraRef = useRef<PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);

  const renderRequestedRef = useRef<boolean>(true);

  const invalidateRender = () => {
    renderRequestedRef.current = true;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    if (rendererRef.current) return undefined;

    const renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      preserveDrawingBuffer: true,
      alpha: false,
    });

    renderer.setClearColor(0xffffff, 1);

    renderer.outputColorSpace = SRGBColorSpace;
    renderer.toneMapping = NoToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = PCFSoftShadowMap;
    renderer.setPixelRatio(1);

    const scene = new Scene();
    scene.background = new Color(0xffffff);

    const camera = new PerspectiveCamera(
      45,
      Math.max(canvas.clientWidth, 1) / Math.max(canvas.clientHeight, 1),
      0.01,
      1000,
    );

    camera.position.set(0, 0, 10);

    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.screenSpacePanning = true;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.enableRotate = true;
    controls.addEventListener('change', invalidateRender);

    rendererRef.current = renderer;
    sceneRef.current = scene;
    cameraRef.current = camera;
    controlsRef.current = controls;

    (window as any).threeRenderer = renderer;
    (window as any).threeScene = scene;
    (window as any).threeCamera = camera;
    (window as any).threeControls = controls;

    const resize = () => {
      const width = Math.max(canvas.clientWidth, 1);
      const height = Math.max(canvas.clientHeight, 1);

      renderer.setSize(width, height, false);

      camera.aspect = width / height;
      camera.updateProjectionMatrix();

      invalidateRender();
    };

    resize();

    callbackOnSceneReady({
      renderer,
      scene,
      camera,
      controls,
      invalidateRender,
    });

    const targetFps = 30;
    const frameIntervalMs = 1000 / targetFps;
    let lastRenderTime = 0;
    let animationFrameId = 0;

    const hasControlsInertia = () => {
      // OrbitControls does not expose Babylon-style inertial offsets.
      // With damping enabled, controls.update() returns true while it still changes the camera.
      return controls.update();
    };

    const renderLoop = () => {
      animationFrameId = requestAnimationFrame(renderLoop);

      if (document.hidden) return;

      if (hasControlsInertia()) {
        renderRequestedRef.current = true;
      }

      if (!renderRequestedRef.current) return;

      const now = performance.now();
      if (now - lastRenderTime < frameIntervalMs) return;

      lastRenderTime = now;
      renderRequestedRef.current = false;

      renderer.render(scene, camera);
    };

    renderLoop();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      resizeObserver.disconnect();

      cancelAnimationFrame(animationFrameId);

      controls.removeEventListener('change', invalidateRender);
      controls.dispose();

      disposeObject(scene);
      renderer.dispose();

      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      controlsRef.current = null;

      (window as any).threeRenderer = undefined;
      (window as any).threeScene = undefined;
      (window as any).threeCamera = undefined;
      (window as any).threeControls = undefined;
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 0,
        objectFit: 'cover',
      }}
    />
  );
}
